'use strict';

/**
 * T1496A / S333 W2 — browser side of the identity-bin PROOF-OF-UNWRAP.
 *
 * `PUT /auth/vault/me/identity-bin` now refuses without `proof` — an Ed25519
 * signature by the key that lives INSIDE vault.bin (`privateKeys.sign.ed25519`,
 * unwrapped at login and held on the in-memory vault session, never persisted)
 * over a server-issued nonce and the digest of the envelope being written.
 * This module mints that proof and carries the reseal: nonce → sign → PUT.
 *
 * ── CUSTODY (the jit-signer idiom, reused not reinvented) ───────────────────
 *   - The private key never leaves the page and is never logged. It is handed
 *     to `createJitSigner({ seed })` — non-extractable WebCrypto Ed25519 where
 *     the engine has it, closure-held fallback otherwise — and the signer is
 *     destroyed right after the one signature.
 *   - `createJitSigner` TRANSFERS its seed (zeroizes the caller's buffer). The
 *     session's key is used by other surfaces for the page lifetime, so a COPY
 *     is transferred and the session's bytes stay intact (pinned by test).
 *   - The message is built by the ONE shared builder the server verifies with
 *     (`identity-bin-proof-message.js`), so the two runtimes cannot drift.
 *
 * ── WHICH KEY (load-bearing) ────────────────────────────────────────────────
 * NOT the jit-signer's HKDF-derived key (`session.jitSigner`) — that keypair is
 * never stored in meta.json, so a signature by it proves nothing about the
 * vault. The server verifies against `meta.publicKeys.sign.ed25519`, whose
 * private half is `session.privateKeys.sign.ed25519`. Fail-closed: absent
 * session / account / key ⇒ a typed error and NO request is sent.
 *
 * Pure + injectable (fetchImpl / subtle / basePrefix) so it is Node-testable
 * and browser-reusable; the bundle wires it to window.fetch + the live session.
 */

const { createJitSigner } = require('./jit-signer.js');
const message = require('./identity-bin-proof-message.js');

function typedError(code, msg) {
  const err = new Error(msg);
  err.code = code;
  return err;
}

/**
 * Mint `{ v, nonce, sig }` for ONE write.
 * @param {object} args
 * @param {string} args.accountNumber
 * @param {string} args.nonce           — from POST /me/identity-bin/nonce
 * @param {object} args.vaultBin        — the envelope about to be PUT
 * @param {Uint8Array} args.signSeed    — session.privateKeys.sign.ed25519 (32 bytes; borrowed, untouched)
 * @param {SubtleCrypto|null} [args.subtle] — injectable; null forces the closure fallback
 */
async function signIdentityBinProof({ accountNumber, nonce, vaultBin, signSeed, subtle } = {}) {
  if (!(signSeed instanceof Uint8Array) || signSeed.length !== 32) {
    throw typedError('ERR_IDENTITY_BIN_SIGNING_KEY_UNAVAILABLE',
      'identity-bin proof: the vault signing key (privateKeys.sign.ed25519) is not in memory');
  }
  const bytes = message.buildProofMessage({ accountNumber, nonce, vaultBin });
  // COPY, then transfer: createJitSigner zeroizes what it is given.
  const signer = await createJitSigner({ seed: new Uint8Array(signSeed), subtle });
  try {
    const sig = await signer.sign(bytes);
    return { v: message.PROOF_VERSION, nonce, sig: message.bytesToHex(sig) };
  } finally {
    signer.destroy();
  }
}

/**
 * The signed reseal: POST nonce → sign → PUT. Resolves on 2xx; throws a typed
 * error (with `status`) otherwise. Never sends the private key.
 *
 * @param {object} args
 * @param {object} args.vaultBin
 * @param {{accountNumber: string, privateKeys?: {sign?: {ed25519?: Uint8Array}}}} args.session
 * @param {Function} [args.fetchImpl]   — defaults to global fetch
 * @param {string} [args.basePrefix]    — app base prefix ('' by default)
 * @param {SubtleCrypto|null} [args.subtle]
 */
async function resealIdentityBinSigned({ vaultBin, session, fetchImpl, basePrefix, subtle } = {}) {
  const f = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  if (!f) throw typedError('ERR_IDENTITY_BIN_RESEAL_NO_FETCH', 'identity-bin reseal: no fetch available');
  if (!vaultBin || typeof vaultBin !== 'object') {
    throw typedError('ERR_IDENTITY_BIN_RESEAL_ENVELOPE', 'identity-bin reseal: vaultBin envelope required');
  }
  const accountNumber = session && session.accountNumber;
  if (typeof accountNumber !== 'string' || accountNumber.length === 0) {
    throw typedError('ERR_IDENTITY_BIN_RESEAL_NO_SESSION', 'identity-bin reseal: no vault session / accountNumber in memory');
  }
  const signSeed = session && session.privateKeys && session.privateKeys.sign && session.privateKeys.sign.ed25519;
  if (!(signSeed instanceof Uint8Array) || signSeed.length !== 32) {
    throw typedError('ERR_IDENTITY_BIN_SIGNING_KEY_UNAVAILABLE',
      'identity-bin reseal: the vault signing key is not in memory — re-unlock the vault first');
  }
  const base = basePrefix || '';
  const json = { 'content-type': 'application/json' };

  const nRes = await f(base + '/auth/vault/me/identity-bin/nonce', {
    method: 'POST', credentials: 'same-origin', headers: json, body: '{}',
  });
  const n = await nRes.json().catch(() => null);
  if (!nRes.ok || !n || n.ok !== true || typeof n.nonce !== 'string') {
    const err = typedError('ERR_IDENTITY_BIN_RESEAL_NONCE', 'identity-bin reseal: nonce request failed: ' + nRes.status);
    err.status = nRes.status;
    throw err;
  }

  const proof = await signIdentityBinProof({ accountNumber, nonce: n.nonce, vaultBin, signSeed, subtle });

  const pRes = await f(base + '/auth/vault/me/identity-bin', {
    method: 'PUT', credentials: 'same-origin', headers: json,
    body: JSON.stringify({ identityVaultBin: vaultBin, proof }),
  });
  if (!pRes.ok) {
    const body = await pRes.json().catch(() => null);
    const err = typedError('ERR_IDENTITY_BIN_RESEAL_REFUSED',
      'identity-bin re-seal failed: ' + pRes.status + (body && body.reason ? ' (' + body.reason + ')' : ''));
    err.status = pRes.status;
    if (body && body.reason) err.reason = body.reason;
    throw err;
  }
  return { ok: true, status: pRes.status };
}

module.exports = { signIdentityBinProof, resealIdentityBinSigned };
